import type { Event } from '@sentry/core';
import { extraErrorDataIntegration, getCurrentScope, init } from '@sentry/react';

import currentUser from '@rover/globals/currentUser';

import sentryBrowserFilterOptions from './sentryBrowserFilterOptions';

const PROD_SENTRY_DSN = 'https://9c4df485116d4051adee5543193eeac3@sentry.io/24119';
const DEV_SENTRY_DSN = 'https://663da0655fda48dd9e79d5d4b0d9d6bd@sentry.io/23944';
const dsn = process.env.NODE_ENV === 'production' ? PROD_SENTRY_DSN : DEV_SENTRY_DSN;

const FILTERED_ERROR_SAMPLE_RATE = 0.01;

const FILTERED_ERROR_REGEX =
  /message has reached max retries|queuemaxretrieserror|InvalidAccessError.*object does not support.*operation/i;

// iOS Safari's microdata extractor (autofill / Reader Mode) throws
// `TypeError: Attempted to assign to readonly property` on Cat in a Flat
// sitter profiles. The throw is internal to Safari and has no user impact;
// the stack frame name is unique so we can drop without collateral. See SSR-T31.
const DROPPED_ERROR_REGEX = /extractFilteredSchemaValuesFromMicroData/i;

function isFilteredErrorEvent(event: Event): boolean {
  return FILTERED_ERROR_REGEX.test(JSON.stringify(event));
}

function isDroppedErrorEvent(event: Event): boolean {
  return DROPPED_ERROR_REGEX.test(JSON.stringify(event));
}

init({
  dsn,
  // Keep Relay inferring an IP for anonymous events, as v8 did; it feeds "users affected".
  dataCollection: { userInfo: true },
  integrations: [extraErrorDataIntegration()],
  beforeSend(event) {
    if (isDroppedErrorEvent(event)) {
      return null;
    }

    // Check if this event contains a filtered error (queue retries, etc.)
    const hasFilteredError = isFilteredErrorEvent(event);

    if (hasFilteredError) {
      // Only report 1% of filtered errors (1 out of 100)
      if (Math.random() > FILTERED_ERROR_SAMPLE_RATE) {
        return null;
      }

      return {
        ...event,
        tags: { ...event.tags, filtered_error_sampled: true },
      };
    }

    return event; // Send all other errors normally
  },
  ...sentryBrowserFilterOptions,
});

getCurrentScope().setTag('team', window.Rover?.context?.pageTeam || 'unknown');

currentUser
  .waitForAuthenticatedUserLoaded()
  // eslint-disable-next-line promise/always-return
  .then((user) => {
    getCurrentScope().setUser({
      id: user.id,
    });
  })
  .catch(() => {
    // Fail silently because it just means the user isn't authenticated
  });
